import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

export type Difficulty = "easy" | "medium" | "hard";

interface Settings {
  difficulty: Difficulty;
  animationsEnabled: boolean;
  soundEnabled: boolean;
}

const defaultSettings: Settings = {
  difficulty: "easy",
  animationsEnabled: true,
  soundEnabled: true,
};

const STORAGE_KEY = "apprendre_settings";

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings;
    const parsed = JSON.parse(raw);
    return {
      difficulty: parsed.difficulty || defaultSettings.difficulty,
      animationsEnabled:
        typeof parsed.animationsEnabled === "boolean"
          ? parsed.animationsEnabled
          : defaultSettings.animationsEnabled,
      soundEnabled:
        typeof parsed.soundEnabled === "boolean"
          ? parsed.soundEnabled
          : defaultSettings.soundEnabled,
    };
  } catch {
    return defaultSettings;
  }
}

function saveSettings(settings: Settings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

// ---- Context ----
interface SettingsContextValue {
  difficulty: Difficulty;
  animationsEnabled: boolean;
  soundEnabled: boolean;
  setDifficulty: (d: Difficulty) => void;
  setAnimationsEnabled: (v: boolean) => void;
  setSoundEnabled: (v: boolean) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  // Broadcast changes via custom event so cross-tab / cross-component sync works
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSettings(loadSettings());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setDifficulty = useCallback((difficulty: Difficulty) => {
    setSettings((prev) => {
      const next = { ...prev, difficulty };
      saveSettings(next);
      return next;
    });
  }, []);

  const setAnimationsEnabled = useCallback((animationsEnabled: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, animationsEnabled };
      saveSettings(next);
      return next;
    });
  }, []);

  const setSoundEnabled = useCallback((soundEnabled: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, soundEnabled };
      saveSettings(next);
      return next;
    });
  }, []);

  const value: SettingsContextValue = {
    difficulty: settings.difficulty,
    animationsEnabled: settings.animationsEnabled,
    soundEnabled: settings.soundEnabled,
    setDifficulty,
    setAnimationsEnabled,
    setSoundEnabled,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return ctx;
}